import React, { useEffect, useState } from "react";
import { Container, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  CalendarEvent,
  Check,
  ChevronDown,
  FilterLeft,
  HourglassSplit,
  PersonSlash,
  Search,
  Share,
  TelephoneOutbound,
  ThreeDots,
  X,
} from "react-bootstrap-icons";
import Dropdown from "react-bootstrap/Dropdown";
import { useBookingQuery } from "../../graphql/booking.hooks";
import moment from "moment";
import Pagination from "../../components/pagination";
import { BookingDetailsModal } from "../../components/modal/bookingDetails";
import { Order, ReservationSortField } from "../../graphql/schema/types";
import NoReservationImg from "./images/no-reservation.png";
import NoRestaurantLogo from "./images/no-restaurant-logo.png";
import LoadingIcon from "./images/loading.svg";
import RebookIcon from "./images/rebook.svg";
import { useSearchParams } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { BookingShareModal } from "../../components/modal/bookingShare";

const Reservations = () => {
  const { t, i18n } = useTranslation();
  const lng = useGetUrlLocale();
  const [limit, setLimit] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [order, setOrder] = useState<Order>(Order.Desc);
  const lang = i18n.language.split("-")[0];
  const [sortField, setSortField] = useState<ReservationSortField>(
    ReservationSortField.CreatedAt
  );
  const [activeFilterLabel, setActiveFilterLabel] = useState(
    t("booking.newest")
  );
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [searchParams] = useSearchParams();
  const { data, loading, refetch } = useBookingQuery({
    variables: {
      search: searchTerm,
      page: currentPage,
      perPage: limit,
      sortField,
      order,
    },
    context: {
      headers: {
        "X-LOCALE": lang,
      },
    },
  });
  const ME_BOOKING_LINK = process.env.REACT_APP_ME_BOOKING_URL;
  const reservations = data?.user?.reservations;
  const pageCount = reservations?.pageCount || 0;
  const totalItems = reservations?.totalCount || 0;
  const firstItemIndex = reservations
    ? (reservations.currentPage - 1) * reservations.itemsPerPage + 1
    : 0;
  const lastItemIndex = reservations
    ? Math.min(reservations.currentPage * reservations.itemsPerPage, totalItems)
    : 0;
  const handleOpenModal = (uuid: string) => {
    setSelectedUuid(uuid);
    setShowModal(true);
  };
  const handleOpenShareModal = (uuid: string) => {
    setSelectedUuid(uuid);
    setShowShareModal(true);
  };
  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetch({
      search: searchTerm,
      page: 1,
      perPage: newLimit,
      sortField,
      order,
    });
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      refetch({
        search: searchTerm,
        page: 1,
        perPage: limit,
        sortField,
        order,
      });
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, limit, sortField, order, refetch]);
  useEffect(() => {
    const shareUuid = searchParams.get("share");
    if (shareUuid && !loading && reservations?.items?.length) {
      setSelectedUuid(shareUuid);
      setShowShareModal(true);
    }
  }, [loading, searchParams, reservations]);
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    refetch({
      search: searchTerm,
      page: newPage,
      perPage: limit,
      sortField,
      order,
    });
  };

  return (
    <Container>
      <h1 className="mb-5 fs-2 mt-5 mt-lg-0">{t("menu.reservations")}</h1>
      {reservations?.totalCount === 0 && searchTerm.trim() === "" ? (
        <>
          <div className="bg-white rounded-4 py-5">
            <div className="col-lg-6 mx-auto py-5 text-center">
              <img src={NoReservationImg} alt="No Reservation Img" />
              <p className="fw-bold fs-4">{t("booking.noReservation")}</p>
              <p className="mb-4 text-gray col-lg-10 mx-auto">
                {t("booking.noReservationDescription")}
              </p>
              <div className="col-lg-4 mx-auto">
                <a
                  href={`${
                    lng === "en"
                      ? process.env.REACT_APP_ME_BOOKING_URL
                      : `${process.env.REACT_APP_ME_BOOKING_URL}/${lng}`
                  }`}
                  className="btn btn-primary py-3 fw-bold text-white w-100"
                >
                  {t("booking.noReservationBtn")}
                </a>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 d-lg-flex justify-content-between">
            <div className="col-lg-3 mb-2 mb-lg-0">
              <div className="col-xxl-9 col-12">
                <div className="bg-white rounded-3 position-relative">
                  <input
                    type="text"
                    className="form-control border-0 py-2"
                    id="search"
                    placeholder={t("booking.search-placeholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Search className="position-absolute translate-middle-y top-50 end-0 me-3" />
                </div>
              </div>
            </div>
            <div>
              <Dropdown className="bg-white rounded-3">
                <Dropdown.Toggle
                  id="dropdown-basic"
                  variant="link"
                  className="no-toggle text-dark text-decoration-none py-2 d-flex justify-content-between align-items-center w-100"
                >
                  <div>
                    <FilterLeft className="me-3" />
                    {activeFilterLabel}
                  </div>
                  <ChevronDown className="ms-2" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.newest")}
                    onClick={() => {
                      setSortField(ReservationSortField.CreatedAt);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("booking.newest"));
                    }}
                  >
                    {t("booking.newest")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.oldest")}
                    onClick={() => {
                      setSortField(ReservationSortField.CreatedAt);
                      setOrder(Order.Asc);
                      setActiveFilterLabel(t("booking.oldest"));
                    }}
                  >
                    {t("booking.oldest")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.name1")}
                    onClick={() => {
                      setSortField(ReservationSortField.RestaurantName);
                      setOrder(Order.Asc);
                      setActiveFilterLabel(t("booking.name1"));
                    }}
                  >
                    {t("booking.name1")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.name2")}
                    onClick={() => {
                      setSortField(ReservationSortField.RestaurantName);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("booking.name2"));
                    }}
                  >
                    {t("booking.name2")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.status")}
                    onClick={() => {
                      setSortField(ReservationSortField.Status);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("booking.status"));
                    }}
                  >
                    {t("booking.status")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.bookingDate1")}
                    onClick={() => {
                      setSortField(ReservationSortField.ReservationAt);
                      setOrder(Order.Asc);
                      setActiveFilterLabel(t("booking.bookingDate1"));
                    }}
                  >
                    {t("booking.bookingDate1")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    active={activeFilterLabel === t("booking.bookingDate2")}
                    onClick={() => {
                      setSortField(ReservationSortField.ReservationAt);
                      setOrder(Order.Desc);
                      setActiveFilterLabel(t("booking.bookingDate2"));
                    }}
                  >
                    {t("booking.bookingDate2")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
          <div className="overflow-auto rounded-bottom-3 scroll-bar">
            <table style={{ minWidth: "1000px" }} className="table mb-0 fs-6">
              <thead>
                <tr>
                  <th className="border-0 fs-5  col-3 col-xxl-3 ps-4 py-3 table-start bg-nav">
                    {t("review.venueName")}
                  </th>
                  <th className="border-0 fs-5 py-3 col-2 bg-nav">
                    {t("booking.date-time")}
                  </th>
                  <th className="border-0 fs-5 py-3 bg-nav col-1">
                    {t("booking.guests")}
                  </th>
                  <th className="border-0 fs-5 py-3 col-2 bg-nav">
                    {t("booking.status")}
                  </th>
                  <th className="border-0 fs-5 py-3 bg-nav table-end col-xxl-3 col-4"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                ) : reservations?.items?.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      <p className="fw-bold fs-4">
                        {t("booking.noSearchResults")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  <>
                    {reservations?.items.map((reservation) => {
                      const requestRestaurantPath =
                        lang === "en"
                          ? `${ME_BOOKING_LINK}/request-restaurant`
                          : `${ME_BOOKING_LINK}/${lang}/request-restaurant`;
                      const rebookHref =
                        reservation.restaurant?.link ||
                        (reservation.customPlace
                          ? `${requestRestaurantPath}?${new URLSearchParams({
                              restaurantName: reservation.customPlace.name,
                              location: reservation.customPlace.location,
                              countryCode:
                                reservation.customPlace.country?.isoCode ?? "",
                            }).toString()}`
                          : requestRestaurantPath);
                      return (
                        <tr key={reservation.uuid}>
                          <td className="py-4 ps-4">
                            <div className="d-flex">
                              <div className="me-3 restaurant-img col-3">
                                <img
                                  loading="lazy"
                                  className="img-fluid"
                                  src={
                                    reservation.restaurant?.logo ||
                                    NoRestaurantLogo
                                  }
                                  alt="Restaurant Img"
                                />
                              </div>
                              <div>
                                <p className="mb-0">
                                  {t("booking.id")}{" "}
                                  <span className="text-primary">
                                    {reservation.id}
                                  </span>
                                </p>
                                <p className="fs-5 mb-0 fw-bold">
                                  {reservation.restaurant?.name ||
                                    reservation.customPlace?.name}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4">
                            <p className="mb-0">
                              {moment(reservation.reservationAt).format(
                                "MMMM D, YYYY"
                              )}
                            </p>
                            <p className="mb-0">
                              {moment(reservation.reservationAt).format(
                                "h:mm A"
                              )}
                              {reservation.flexibility !== "NONE" && (
                                <span className="fs-7 ms-2 d-inline-block px-2 rounded bg-primary-light text-primary">
                                  ± {reservation.flexibility.slice(5, 6)}
                                </span>
                              )}
                            </p>
                          </td>
                          <td className="py-4">
                            {reservation.guestsCount}{" "}
                            {reservation.guestsCount === 1
                              ? t("booking.guest")
                              : t("booking.guests")}
                          </td>
                          <td className="py-4">
                            {reservation.status === "PENDING" && (
                              <span
                                className="px-3 rounded bg-light-blue text-blue d-inline-block text-nowrap"
                                style={{ padding: "7px" }}
                              >
                                <HourglassSplit className="me-2" />
                                {t("booking.pending")}
                              </span>
                            )}
                            {reservation.status === "WAITING_FOR_OPENING" && (
                              <span
                                className="px-3 rounded bg-light text-secondary d-inline-block text-nowrap"
                                style={{ padding: "7px" }}
                              >
                                <HourglassSplit className="me-2" />
                                {t("booking.waitingForOpening")}
                              </span>
                            )}
                            {reservation.status === "IN_PROGRESS" && (
                              <span
                                className="px-3 rounded bg-light-purple text-purple d-inline-flex align-items-center"
                                style={{ padding: "7px" }}
                              >
                                <img
                                  src={LoadingIcon}
                                  alt="LoadingIcon"
                                  className="me-2"
                                />
                                {t("booking.inProgress")}
                              </span>
                            )}
                            {reservation.status === "CONFIRMED" && (
                              <span
                                className="px-3 rounded bg-success-light text-success d-inline-block text-nowrap"
                                style={{ padding: "7px" }}
                              >
                                <Check className="me-2" />
                                {t("booking.confirmed")}
                              </span>
                            )}
                            {reservation.status === "NO_CONNECTION" && (
                              <span
                                className="px-3 rounded bg-warning-light text-warning d-inline-block"
                                style={{ padding: "7px" }}
                              >
                                <TelephoneOutbound className="me-2" />
                                {t("booking.unreachable")}
                              </span>
                            )}
                            {reservation.status === "REJECTED" && (
                              <span
                                className="px-3 rounded bg-primary-light text-primary d-inline-block"
                                style={{ padding: "7px" }}
                              >
                                <X className="me-2" />
                                {t("booking.canceled")}
                              </span>
                            )}
                            {reservation.status === "CANCELLED_BY_USER" && (
                              <span
                                className="px-3 rounded bg-additional-gray text-dark d-inline-block text-nowrap"
                                style={{ padding: "7px" }}
                              >
                                <PersonSlash className="me-2" />
                                {t("booking.userCanceled")}
                              </span>
                            )}
                          </td>
                          <td className="py-4 pe-4 ps-xxl-0 ps-4">
                            <div className="row gx-3 px-2 justify-content-end">
                              <div className="d-flex gap-2 px-0 col-8 justify-content-end">
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {t("booking.shareBooking")}
                                    </Tooltip>
                                  }
                                >
                                  <button
                                    className="btn-outline-primary btn"
                                    onClick={() =>
                                      handleOpenShareModal(reservation?.uuid)
                                    }
                                  >
                                    <Share />
                                  </button>
                                </OverlayTrigger>
                                <Dropdown>
                                  <OverlayTrigger
                                    placement="top"
                                    overlay={
                                      <Tooltip>
                                        {t("booking.addToCalendar")}
                                      </Tooltip>
                                    }
                                  >
                                    <Dropdown.Toggle
                                      variant="outline-primary"
                                      id={`calendar-dropdown-${reservation.uuid}`}
                                      className="no-toggle h-100"
                                    >
                                      <CalendarEvent />
                                      <ChevronDown className="ms-2" />
                                    </Dropdown.Toggle>
                                  </OverlayTrigger>
                                  <Dropdown.Menu className="rounded-4 mt-2">
                                    <Dropdown.Item
                                      href={reservation.calendarGoogleUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={() =>
                                        window.gtag?.(
                                          "event",
                                          "calendar_google_clicked"
                                        )
                                      }
                                    >
                                      {t("booking.addToGoogleCalendar")}
                                    </Dropdown.Item>
                                    <Dropdown.Item
                                      href={reservation?.calendarIcsUrl}
                                      onClick={() =>
                                        window.gtag?.(
                                          "event",
                                          "calendar_ics_downloaded"
                                        )
                                      }
                                    >
                                      {t("booking.addToIcsCalendar")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>
                                      {t("booking.bookingDetails")}
                                    </Tooltip>
                                  }
                                >
                                  <button
                                    className="btn-outline-primary btn"
                                    onClick={() =>
                                      handleOpenModal(reservation?.uuid)
                                    }
                                  >
                                    <ThreeDots />
                                  </button>
                                </OverlayTrigger>
                                <OverlayTrigger
                                  placement="top"
                                  overlay={
                                    <Tooltip>{t("booking.rebook")}</Tooltip>
                                  }
                                >
                                  <a
                                    href={rebookHref}
                                    className="btn-primary btn fw-bold text-white px-2 fs-6"
                                  >
                                    <img src={RebookIcon} alt="RebookIcon" />
                                  </a>
                                </OverlayTrigger>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            t={t}
            pages={pageCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
          <BookingDetailsModal
            showModal={showModal}
            onCancel={() => setShowModal(false)}
            reservations={reservations?.items ?? []}
            uuid={selectedUuid}
            refetch={refetch}
          />
          <BookingShareModal
            showModal={showShareModal}
            onCancel={() => setShowShareModal(false)}
            reservations={reservations?.items ?? []}
            uuid={selectedUuid}
          />
        </>
      )}
    </Container>
  );
};

export default Reservations;
